// ─────────────────────────────────────────────────────────────────────────────
// SettingsPage — a PAGE ON THE DOMAIN BAR, NOT A DOMAIN.
//
// SETTINGS is deliberately NOT a tenth entry in DOMAINS (lib/agents.ts). It has no
// agent, no dashboard, no chat and no activity board, and 31 hardcoded domain
// literals across the app behave conditionally on the domain value — AppShell gates
// chat on HOMEPAGE, useRexySocket filters by domain equality. A tenth domain would
// mean teaching every one of them that one of them is special.
//
// So this is a VIEW that replaces the domain content while `activeDomain` stays
// exactly what it was. Every domain conditional is untouched, and because the view
// is independent of the domain, the SAME page mirrors into every domain — one page,
// reachable from anywhere on the bar.
//
// NOTHING HERE IS NEW WIRING. The Audio I/O panel moves whole, with its five
// controls still writing over the daemon control WebSocket via voicePanelPrefs; the
// Core Connection detail moves whole, with its own socket listeners. UI moved; the
// backend did not.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../../lib/rexyConsole';
import { useAppShellLive } from '../shell/AppShellContext';
import AudioIOCard from '../AudioIOCard';
import CoreConnectionCard from '../CoreConnectionCard';
import DiagnosticsCard from '../DiagnosticsCard';
import NetworkCard from '../NetworkCard';

export default function SettingsPage() {
  const { meter } = useAppShellLive();

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <span style={styles.title}>SETTINGS</span>
        <span style={styles.subtitle}>audio i/o · core connection · diagnostics · network</span>
      </header>

      <div style={styles.grid}>
        {/* The whole Audio I/O panel — noise suppression, echo cancellation, input
            sensitivity threshold, sensitivity mode, input device. Its writes go over
            the daemon control WebSocket exactly as they did on the Console. The
            Local/Deepgram engine indicator is read-only display, not a control. */}
        <AudioIOCard meter={meter} />

        {/* The Core Connection DETAIL — core, endpoint, path, transport, reconnects,
            last heartbeat. The Console keeps a one-line version of the same card. */}
        <CoreConnectionCard variant="detail" />

        {/* DIAGNOSTICS and NETWORK — restored from HEAD, byte-unchanged, and mounted
            here rather than left deleted. THIS ROUND IS A MOVE, NOT A DELETE:
            "unused" is not "gone". Socket.IO status, heartbeat, reconnect count,
            transport, VPS, packets and uptime are exactly the detail that leaves a
            one-line Core Connection on the Console and belongs on a settings page,
            next to the Core Connection detail it sits beside. */}
        <DiagnosticsCard />
        <NetworkCard />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    height: '100%',
    overflowY: 'auto',
    padding: '18px 20px 28px',
    fontFamily: REXY_FONT,
    color: REXY_CONSOLE.secondary,
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 16,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: 3,
    color: REXY_CONSOLE.primary,
  },
  subtitle: {
    fontSize: 10,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.mutedText,
    textTransform: 'uppercase',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: 14,
    alignItems: 'start',
    maxWidth: 1100,
  },
};
